import {
  mcGetCloudItem,
  mcGetItem,
  mcSetCloudItemIfRevision,
  mcSetCachedItem,
  mcSubscribeCloudItem,
  mcRemoveItem,
} from "./mcCloudSync";

/**
 * Shared Ordering Service
 *
 * Phase A foundation for INF-SC-003. This module intentionally has no UI
 * dependency. It owns the site-scoped category ordering document and exposes
 * one API for load, save, subscribe and broadcast.
 */

export const MC_SHARED_ORDERING_EVENT = "mc-shared-ordering-change";
export const MC_SHARED_ORDERING_KEY = "mc-shared-category-order";

const cleanText = (value) => String(value || "").trim();

function normalizeScope(scope = "ALL") {
  const raw = cleanText(scope);
  if (!raw || raw.toUpperCase() === "ALL") return "ALL";
  return raw
    .replace(/^inventory_/i, "")
    .replace(/_Store$/i, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "ALL";
}

export function sharedOrderingStorageKey(scope = "ALL") {
  const normalizedScope = normalizeScope(scope);
  return normalizedScope === "ALL"
    ? MC_SHARED_ORDERING_KEY
    : `${MC_SHARED_ORDERING_KEY}:${normalizedScope}`;
}

export function normalizeSharedOrdering(order = []) {
  const seen = new Set();
  return (Array.isArray(order) ? order : [])
    .map(cleanText)
    .filter((item) => {
      if (!item) return false;
      const key = item.toLowerCase().replace(/\s+/g, " ");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function parseOrdering(raw) {
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) return normalizeSharedOrdering(parsed);
    if (Array.isArray(parsed?.order)) return normalizeSharedOrdering(parsed.order);
  } catch (_) {}
  return [];
}

export function parseSharedOrderingRecord(raw) {
  if (!raw) return { order: [], payload: null, revision: 0 };
  try {
    const payload = typeof raw === "string" ? JSON.parse(raw) : raw;
    const order = Array.isArray(payload) ? normalizeSharedOrdering(payload) : normalizeSharedOrdering(payload?.order || []);
    return { order, payload: Array.isArray(payload) ? null : payload, revision: Number(payload?.revision || 0) };
  } catch (_) {
    return { order: [], payload: null, revision: 0 };
  }
}

function buildPayload(order = [], scope = "ALL", metadata = {}) {
  return {
    version: 1,
    scope: normalizeScope(scope),
    order: normalizeSharedOrdering(order),
    updatedAt: new Date().toISOString(),
    updatedBy: cleanText(metadata.updatedBy || metadata.user || metadata.email),
    source: cleanText(metadata.source || "inventory-configuration"),
    writeToken: cleanText(metadata.writeToken),
    clientId: cleanText(metadata.clientId),
    revision: Number(metadata.revision || 0),
  };
}

function dispatchOrderingEvent(payload, origin = "local") {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(MC_SHARED_ORDERING_EVENT, {
    detail: {
      ...payload,
      origin,
    },
  }));
}

export function peekSharedOrdering(scope = "ALL") {
  const key = sharedOrderingStorageKey(scope);
  const raw = mcGetItem(key);
  return parseOrdering(raw);
}

export async function loadSharedOrderingRecord(scope = "ALL", options = {}) {
  const key = sharedOrderingStorageKey(scope);
  const preferCloud = options.preferCloud !== false;
  if (preferCloud) {
    const cloudRaw = await mcGetCloudItem(key);
    if (cloudRaw !== null) {
      const serialized = typeof cloudRaw === "string" ? cloudRaw : JSON.stringify(cloudRaw);
      mcSetCachedItem(key, serialized);
      return parseSharedOrderingRecord(cloudRaw);
    }
  }
  return parseSharedOrderingRecord(mcGetItem(key));
}

export async function loadSharedOrdering(scope = "ALL", options = {}) {
  const key = sharedOrderingStorageKey(scope);
  const preferCloud = options.preferCloud !== false;

  if (preferCloud) {
    const cloudRaw = await mcGetCloudItem(key);
    const cloudOrder = parseOrdering(cloudRaw);
    if (cloudRaw !== null) {
      const payload = typeof cloudRaw === "string" ? cloudRaw : JSON.stringify(cloudRaw);
      mcSetCachedItem(key, payload);
      return cloudOrder;
    }
  }

  return peekSharedOrdering(scope);
}

export async function saveSharedOrdering(order = [], scope = "ALL", metadata = {}) {
  const key = sharedOrderingStorageKey(scope);
  const payload = buildPayload(order, scope, metadata);
  const serialized = JSON.stringify(payload);

  // Cache immediately so the initiating browser remains responsive.
  mcSetCachedItem(key, serialized);
  dispatchOrderingEvent(payload, "local");

  // Phase A establishes an explicit Firestore-backed write contract. The
  // caller receives the verification result instead of assuming persistence.
  const guarded = await mcSetCloudItemIfRevision(key, serialized, Number(metadata.baseRevision || 0));
  if (!guarded.ok) {
    if (guarded.currentValue) mcSetCachedItem(key, guarded.currentValue);
    const current = parseSharedOrderingRecord(guarded.currentValue);
    return {
      ok: false,
      verified: false,
      conflict: Boolean(guarded.conflict),
      currentOrder: current.order,
      currentPayload: current.payload,
      revision: Number(guarded.revision || current.revision || 0),
      key,
      payload,
    };
  }
  const cloudSaved = true;
  payload.revision = Number(guarded.revision || 0);
  if (guarded.currentValue) mcSetCachedItem(key, guarded.currentValue);

  // Global publishing only needs the authoritative cloud write acknowledgement.
  // Skip the slower read-back loop so a successful write is not reported as a
  // timeout while Firestore listeners are still converging.
  if (metadata.skipVerification === true) {
    return {
      ok: true,
      cloudSaved: true,
      verified: false,
      verificationSkipped: true,
      conflict: false,
      revision: Number(payload.revision || 0),
      key,
      payload,
    };
  }

  // Phase D persistence verification must not depend exclusively on the
  // realtime listener. A listener can be delayed, coalesced, or attach after
  // the write, which previously left the Response Designed card stuck on
  // "Saving ordering…" even though Firestore had already accepted the order.
  // Read the document back and verify the exact token + order here.
  let verified = false;
  for (let attempt = 0; attempt < 6 && !verified; attempt += 1) {
    if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
    const cloudRaw = await mcGetCloudItem(key);
    if (cloudRaw === null) continue;
    try {
      const cloudPayload = typeof cloudRaw === "string" ? JSON.parse(cloudRaw) : cloudRaw;
      const cloudOrder = normalizeSharedOrdering(cloudPayload?.order || cloudPayload || []);
      verified = cleanText(cloudPayload?.writeToken) === cleanText(payload.writeToken)
        && JSON.stringify(cloudOrder) === JSON.stringify(payload.order);
    } catch (_) {}
  }

  return {
    ok: Boolean(cloudSaved && verified),
    cloudSaved: Boolean(cloudSaved),
    verified,
    conflict: false,
    revision: Number(payload.revision || 0),
    key,
    payload,
  };
}

export function removeSharedOrderingOverride(scope = "ALL") {
  const normalizedScope = normalizeScope(scope);
  if (normalizedScope === "ALL") return false;
  mcRemoveItem(sharedOrderingStorageKey(normalizedScope));
  return true;
}

export function broadcastSharedOrdering(order = [], scope = "ALL", metadata = {}) {
  const payload = buildPayload(order, scope, metadata);
  dispatchOrderingEvent(payload, "broadcast");
  return payload;
}

export function subscribeSharedOrdering(scope = "ALL", callback) {
  if (typeof callback !== "function") return () => {};

  const key = sharedOrderingStorageKey(scope);
  const normalizedScope = normalizeScope(scope);
  let lastSignature = "";

  const emit = (order, meta = {}) => {
    const normalizedOrder = normalizeSharedOrdering(order);
    const writeToken = cleanText(meta?.payload?.writeToken);
    const signature = JSON.stringify({ order: normalizedOrder, writeToken });
    if (signature === lastSignature && !meta.force) return;
    lastSignature = signature;
    callback(normalizedOrder, {
      scope: normalizedScope,
      key,
      ...meta,
    });
  };

  const onLocalEvent = (event) => {
    const detail = event?.detail || {};
    if (normalizeScope(detail.scope) !== normalizedScope) return;
    emit(detail.order || [], { origin: detail.origin || "local-event", payload: detail });
  };

  const onStorage = (event) => {
    if (event?.key !== key) return;
    emit(parseOrdering(event.newValue), { origin: "storage" });
  };

  if (typeof window !== "undefined") {
    window.addEventListener(MC_SHARED_ORDERING_EVENT, onLocalEvent);
    window.addEventListener("storage", onStorage);
  }

  const unsubscribeCloud = mcSubscribeCloudItem(key, (raw, cloudMeta = {}) => {
    if (raw === null) {
      emit([], { origin: "cloud", cloudMeta });
      return;
    }
    const order = parseOrdering(raw);
    let parsedPayload = null;
    try {
      parsedPayload = typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch (_) {}
    // Keep the browser cache aligned with the cloud snapshot without routing
    // this through the service save path and creating a write loop.
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      const payload = parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : buildPayload(order, scope, { source: "cloud-subscription" });
      mcSetCachedItem(key, JSON.stringify(payload));
    } catch (_) {}
    emit(order, { origin: "cloud", cloudMeta, payload: parsedPayload });
  });

  // Supply the cached value immediately while Firestore hydrates.
  emit(peekSharedOrdering(scope), { origin: "cache", force: true });

  return () => {
    if (typeof window !== "undefined") {
      window.removeEventListener(MC_SHARED_ORDERING_EVENT, onLocalEvent);
      window.removeEventListener("storage", onStorage);
    }
    if (typeof unsubscribeCloud === "function") unsubscribeCloud();
  };
}

export default {
  keyForScope: sharedOrderingStorageKey,
  normalize: normalizeSharedOrdering,
  peek: peekSharedOrdering,
  load: loadSharedOrdering,
  loadRecord: loadSharedOrderingRecord,
  save: saveSharedOrdering,
  subscribe: subscribeSharedOrdering,
  broadcast: broadcastSharedOrdering,
};
